import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
// Import doctorsData
import { doctorsData } from './Doctor';

const DoctorProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('bio');
    const [doctor, setDoctor] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Find doctor from doctorsData using the id from URL
        const doctorId = parseInt(id);
        const foundDoctor = doctorsData.find(doc => doc.id === doctorId);

        if (foundDoctor) {
            // Transform the data to match the profile structure
            const transformedDoctor = {
                ...foundDoctor,
                details: {
                    specialties: [foundDoctor.specialty],
                    education: [foundDoctor.designation],
                    experience: [`${foundDoctor.experience} of experience at ${foundDoctor.hospital}`],
                    languages: ["English", "Hindi"]
                },
                blogs: [
                    {
                        title: `Latest Updates in ${foundDoctor.specialty}`,
                        date: new Date().toLocaleDateString(),
                        excerpt: foundDoctor.description
                    }
                ]
            };
            setDoctor(transformedDoctor);
        }
        setLoading(false);
    }, [id]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="flex items-center justify-center min-h-screen">
                    <div className="w-16 h-16 border-4 rounded-full border-t-blue-500 border-b-blue-500 animate-spin"></div>
                </div>
                <Footer />
            </>
        );
    }

    if (!doctor) {
        return (
            <>
                <Navbar />
                <div className="container max-w-6xl px-4 mx-auto my-8">
                    <div className="text-center py-12">
                        <h2 className="text-2xl font-bold text-gray-800">Doctor not found</h2>
                        <p className="mt-2 text-gray-600">The doctor you're looking for doesn't exist.</p>
                        <button
                            onClick={() => navigate('/doctor')}
                            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                        >
                            Back to Doctors
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />

            <div className="container max-w-6xl px-4 mx-auto my-8">
                {/* Breadcrumb */}
                <div className="flex items-center gap-1 mb-6 text-sm text-gray-500">
                    <Link to="/" className="text-teal-600 hover:text-teal-700">Home</Link>
                    <span>/</span>
                    <Link to="/doctor" className="text-teal-600 hover:text-teal-700">Doctors</Link>
                    <span>/</span>
                    <span>{doctor.name}</span>
                </div>

                <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                    {/* Left Column - Doctor Info */}
                    <div className="flex flex-col items-center p-6 text-center bg-white rounded-lg shadow-md">
                        <div className="w-32 h-32 mb-4 overflow-hidden rounded-full">
                            <img
                                src={doctor.imageUrl}
                                alt={doctor.name}
                                className="object-cover w-full h-full"
                            />
                        </div>

                        <h1 className="text-xl font-bold text-[#1B3C74]">{doctor.name}</h1>
                        <p className="mt-1 text-sm text-[#1B3C74]/80">{doctor.designation}</p>

                        <div className="mt-2 text-sm text-gray-600">
                            <p>{doctor.description}</p>
                        </div>
                    </div>

                    {/* Right Column - Tabs and Content */}
                    <div className="col-span-2">
                        {/* Tabs */}
                        <div className="flex mb-6 border-b border-gray-200">
                            <button
                                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'bio'
                                    ? 'text-[#009DA5] border-b-2 border-[#009DA5]'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => setActiveTab('bio')}
                            >
                                Bio
                            </button>
                            <button
                                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'details'
                                    ? 'text-[#009DA5] border-b-2 border-[#009DA5]'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => setActiveTab('details')}
                            >
                                Other Details
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="p-1">
                            {activeTab === 'bio' && (
                                <div>
                                    <p className="text-gray-700 whitespace-pre-line">
                                        {doctor.description}
                                        {'\n\n'}
                                        Dr. {doctor.name} is a highly skilled {doctor.specialty} specialist at {doctor.hospital}.
                                        With {doctor.experience} of experience, they have established themselves as a respected
                                        medical professional in their field.
                                    </p>
                                </div>
                            )}

                            {activeTab === 'details' && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-[#1B3C74]">Specialty</h3>
                                        <ul className="pl-5 list-disc">
                                            <li className="mb-1 text-gray-700">{doctor.specialty}</li>
                                        </ul>
                                    </div>

                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-[#1B3C74]">Qualification</h3>
                                        <ul className="pl-5 list-disc">
                                            <li className="mb-1 text-gray-700">{doctor.designation}</li>
                                        </ul>
                                    </div>

                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-[#1B3C74]">Experience</h3>
                                        <ul className="pl-5 list-disc">
                                            <li className="mb-1 text-gray-700">{doctor.experience} at {doctor.hospital}</li>
                                        </ul>
                                    </div>

                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-[#1B3C74]">Available Timings</h3>
                                        <div className="pl-5">
                                            <p className="text-gray-700">{doctor.nextAvailable}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default DoctorProfile;